import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Input } from './ui/input';
import { SSCoinIcon } from './SSCoin';
import { Search, Star, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const COIN_RATE = 10;

export default function SearchAutocomplete({ isAuthenticated }) {
  const { isRTL } = useLanguage();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Animated placeholder
  const placeholders = isRTL
    ? ['ابحث عن حقائب...', 'ابحث عن إلكترونيات...', 'ابحث عن أزياء...', 'ابحث عن تجميل...', 'ابحث عن رياضة...']
    : ['Search bags...', 'Search electronics...', 'Search fashion...', 'Search beauty...', 'Search sports...'];
  const [phIdx, setPhIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setPhIdx(p => (p + 1) % placeholders.length), 5000);
    return () => clearInterval(t);
  }, []);

  // Close on click outside
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Search with debounce
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (query.trim().length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/products?search=${encodeURIComponent(query)}&limit=8`);
        const data = await res.json();
        setResults(data.products || []);
        setOpen(true);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    }, 300);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  const handleSelect = (product) => {
    setOpen(false);
    setQuery('');
    navigate(`/product/${product.id}`);
  };

  return (
    <div ref={wrapperRef} className="relative hidden sm:block max-w-xl flex-1">
      <Search className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground z-10 ${isRTL ? 'right-4' : 'left-4'}`} />
      {loading && <Loader2 className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-primary animate-spin z-10 ${isRTL ? 'left-3' : 'right-3'}`} />}
      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => { if (results.length > 0) setOpen(true); }}
        placeholder={placeholders[phIdx]}
        className={`${isRTL ? 'pr-12' : 'pl-12'} h-11 bg-secondary/50 border-border hover:border-primary/40 focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-xl text-base transition-all shadow-sm`}
        data-testid="global-search-input"
      />

      {/* Dropdown Results */}
      <AnimatePresence>
        {open && results.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -5, scaleY: 0.95 }}
            animate={{ opacity: 1, y: 0, scaleY: 1 }}
            exit={{ opacity: 0, y: -5, scaleY: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full mt-1 w-full bg-card border border-border rounded-xl shadow-2xl shadow-black/30 overflow-hidden z-50"
            style={{ transformOrigin: 'top' }}
          >
            <div className="max-h-[400px] overflow-y-auto">
              {results.map((product, i) => (
                <button
                  key={product.id || i}
                  onClick={() => handleSelect(product)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-primary/5 transition-colors text-left border-b border-border/50 last:border-0"
                >
                  <img
                    src={product.image}
                    alt=""
                    className="w-12 h-12 rounded-lg object-cover flex-shrink-0 bg-secondary"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {isRTL ? product.name_ar : product.name}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-muted-foreground bg-secondary px-1.5 py-0.5 rounded">
                        {isRTL ? product.category_name_ar : product.category_name}
                      </span>
                      <div className="flex items-center gap-0.5">
                        <Star className="w-3 h-3 fill-current text-amber-400" />
                        <span className="text-[10px] text-muted-foreground">{product.rating}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    {isAuthenticated ? (
                      <div className="flex items-center gap-1">
                        <SSCoinIcon size={14} />
                        <span className="font-mono text-sm font-bold text-amber-400">
                          {Math.round(product.buy_price * COIN_RATE)}
                        </span>
                      </div>
                    ) : (
                      <span className="font-mono text-sm font-bold text-primary">${product.sell_price}</span>
                    )}
                    <p className="text-[9px] text-[hsl(var(--success))]">+{product.profit_percent}%</p>
                  </div>
                </button>
              ))}
            </div>

            {/* Search all button */}
            <button
              onClick={() => { navigate(`/products?search=${encodeURIComponent(query)}`); setOpen(false); setQuery(''); }}
              className="w-full p-2.5 text-center text-xs font-medium text-primary bg-primary/5 hover:bg-primary/10 transition-colors"
            >
              {isRTL ? `عرض جميع النتائج لـ "${query}"` : `View all results for "${query}"`}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* No results */}
      <AnimatePresence>
        {open && query.length >= 2 && results.length === 0 && !loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute top-full mt-1 w-full bg-card border border-border rounded-xl shadow-xl p-4 text-center z-50"
          >
            <p className="text-sm text-muted-foreground">
              {isRTL ? 'لا توجد نتائج' : 'No results found'}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
